import {
  Badge,
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { StyledNavLink } from './StyledNavLink';
import { IRoute } from '../../interfaces';

interface MenuListItemProps {
  route: IRoute;
  /** Waiting count for this route. Nothing is rendered when it is zero. */
  badgeCount?: number;
  /** Paint the count in the highlight tint so it reads before the others. */
  hot?: boolean;
  /** Labels are shown; collapsed rails get a tooltip and an icon badge instead. */
  expanded: boolean;
  /** Only the exact path counts as active — for links that prefix other menu routes. */
  exact?: boolean;
}

/** Shared by every row in the navy rail: nav routes, the Reva tile, the account block. */
export const railItemSx = {
  minHeight: 40,
  borderRadius: '9px',
  px: 1.5,
  color: 'rgba(255,255,255,0.78)',
  position: 'relative' as const,
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.07)',
    color: '#fff',
  },
  '&.active, &.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: 'rgba(255,255,255,0.12)',
    color: '#fff',
    '& .MuiListItemText-primary': { fontWeight: 600 },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -12,
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: '0 3px 3px 0',
      backgroundColor: 'primary.main',
    },
  },
};

export const MenuListItem: React.FC<MenuListItemProps> = ({
  route,
  badgeCount = 0,
  hot = false,
  expanded,
  exact = false,
}) => {
  const countColors = hot
    ? { backgroundColor: 'highlight.main', color: '#111' }
    : { backgroundColor: 'rgba(255,255,255,0.14)', color: '#fff' };

  return (
    <Tooltip
      title={expanded ? '' : route.title}
      placement="right"
      disableHoverListener={expanded}
      disableFocusListener={expanded}
    >
      <ListItemButton
        component={StyledNavLink}
        to={route.path}
        end={exact}
        sx={{
          ...railItemSx,
          justifyContent: expanded ? 'flex-start' : 'center',
          px: expanded ? 1.5 : 0,
          width: expanded ? 'auto' : 48,
          mx: expanded ? 0 : 'auto',
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: expanded ? 1.5 : 0,
            color: 'inherit',
            justifyContent: 'center',
          }}
        >
          <Badge
            data-testid={`nav-badge-${route.key}`}
            // Undefined, not 0: a zero would still put a hidden "0" in the DOM.
            badgeContent={badgeCount > 0 ? badgeCount : undefined}
            invisible={badgeCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                ...countColors,
                fontWeight: 700,
                border: '2px solid',
                borderColor: 'secondary.main',
                // Expanded rows show the count at the right instead. Hidden
                // with CSS, not `invisible`, so the badge text keeps updating.
                display: expanded ? 'none' : undefined,
              },
            }}
          >
            <route.icon sx={{ fontSize: 22 }} />
          </Badge>
        </ListItemIcon>
        <ListItemText
          primary={route.title}
          primaryTypographyProps={{
            noWrap: true,
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
          sx={{ my: 0, display: expanded ? 'block' : 'none' }}
        />
        {expanded && badgeCount > 0 && (
          <Box
            component="span"
            sx={{
              ...countColors,
              ml: 'auto',
              minWidth: 20,
              height: 20,
              px: 0.75,
              borderRadius: 10,
              fontSize: '0.72rem',
              fontWeight: 700,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {badgeCount}
          </Box>
        )}
      </ListItemButton>
    </Tooltip>
  );
};
